import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { ApiError } from "@/api/fetch";
import { Button } from "@/components/ui/button";
import { WorkspaceShell } from "@/components/layout/WorkspaceShell";
import { OrgProvider, orgQuery, setLastOrgSlug, clearLastOrgSlug } from "@/lib/org/OrgContext";

/**
 * Workspace layout route. Pre-fetches the organization in beforeLoad so child
 * routes can call useOrg() synchronously without each fetching it themselves.
 * Auth gate lives here too — every nested route inherits.
 */
export const Route = createFileRoute("/o/$slug")({
  beforeLoad: async ({ context, params }) => {
    if (!context.auth.isAuthenticated()) {
      throw redirect({ to: "/login" });
    }
    // ensureQueryData throws on network/HTTP error — TanStack Router catches
    // it and routes to errorComponent below.
    await context.queryClient.ensureQueryData(orgQuery(params.slug));
    // Remember this slug so the next bare-root visit lands here automatically.
    // Only persist once we've confirmed the org is reachable.
    setLastOrgSlug(params.slug);
  },
  component: WorkspaceLayout,
  errorComponent: WorkspaceErrorBoundary,
});

function WorkspaceLayout() {
  const { slug } = Route.useParams();
  // Guaranteed cache hit thanks to beforeLoad. We still read via useQuery so
  // background invalidations (settings page renaming the org, etc.) propagate
  // through the shell without a route reload.
  const { data: org } = useQuery({ ...orgQuery(slug) });

  if (!org) {
    // Unreachable under normal flow — beforeLoad would've thrown if the fetch
    // failed. Keep the type-narrowing fallback so consumers don't have to
    // null-check useOrg().
    return null;
  }

  return (
    <OrgProvider value={org}>
      <WorkspaceShell />
    </OrgProvider>
  );
}

function WorkspaceErrorBoundary({ error }: { error: Error }) {
  const navigate = useNavigate();
  const status = error instanceof ApiError ? error.status : null;
  const isNotFound = status === 404;
  const isForbidden = status === 403;

  return (
    <div className="grid min-h-screen place-items-center bg-muted/30 p-6">
      <div className="w-full max-w-md rounded-lg border bg-card p-8 shadow-sm">
        <div className="flex items-center gap-2 text-destructive">
          <AlertTriangle className="h-5 w-5" />
          <h1 className="text-lg font-semibold">
            {isNotFound
              ? "Organisation nicht gefunden"
              : isForbidden
                ? "Kein Zugriff"
                : "Etwas ist schiefgelaufen"}
          </h1>
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          {isNotFound
            ? "Diese Organisation existiert nicht oder ist nicht (mehr) für dich sichtbar."
            : isForbidden
              ? "Du bist nicht Mitglied dieser Organisation."
              : error.message}
        </p>
        <Button
          className="mt-6 w-full"
          onClick={() => {
            // Forget the slug that just failed so the root redirect can fall
            // back to the first reachable org instead of looping back here.
            clearLastOrgSlug();
            void navigate({ to: "/" });
          }}
        >
          Zur Startseite
        </Button>
      </div>
    </div>
  );
}
